import { client } from "@/client";
import { useMutation } from "@apollo/client/react";
import {
  AddShiftMutation,
  AddShiftMutationVariables,
  AddShiftDocument,
  FetchTenderByIdDocument,
  FetchServicesOnTenderDocument,
} from "@/graphql/__generated__/graphql";

/**
 * Custom hook for adding a shift to a service and handling the necessary cache updates.
 * @param {number} serviceId - ID of Service to updated in the cache.
 * @param {number} _tenderId - ID of Tender to updated in the cache.
 * @returns {Object} - Contains the addShiftMutation function, loading state, and error state.
 */
export default function useAddShiftMutationHook(serviceId: number, _tenderId: number): { addShiftMutation: any, loading: any, error: any } {
  const [addShiftMutation, { loading, error }] = useMutation<AddShiftMutation, AddShiftMutationVariables>(
    AddShiftDocument, {
      onCompleted: (data) => {
        updateCache(data, serviceId);
      },
      refetchQueries: [
        {
          query: FetchTenderByIdDocument,
          variables: { id: _tenderId }
        },
        {
          query: FetchServicesOnTenderDocument,
          variables: { tenderId: Number(_tenderId) }
        }
      ]
    });

  /**
   * Adds reference of new shift to the service's shifts array.
   * @param {AddShiftMutation} newData - Response from the mutation
   * @param {number} serviceId - The ID of the service to update in the cache.
   */
  const updateCache = (newData: AddShiftMutation, serviceId: number) => {
    const shiftId = newData.addShift.shifts[0].id;
    // Get the cache reference of the new Shift
    const newShiftReference = client.cache.identify({
      __typename: "Shift",
      id: shiftId
    });

    // Update the `shifts` field of the Service object
    client.cache.modify({
      id: client.cache.identify({ __typename: "Service", id: serviceId }),
      fields: {
        shifts(existingShifts = []) {
          if (!existingShifts) {
            return [newShiftReference];
          }

          return [...existingShifts, { __ref: newShiftReference }];
        }
      }
    });
  };

  return {
    addShiftMutation,
    loading,
    error
  };
}
